import React, { KeyboardEvent } from 'react';
import Button from './Button';

interface SavedSearch {
  id: string;
  name: string;
  searchTerms: string[];
  radius: number;
  customerId?: string;
  location?: { lat: number; lng: number };
  createdAt: number;
}

interface Vertical {
  id: string;
  name: string;
  description: string;
  terms?: string[];
}

interface SearchPanelProps {
  // Location context
  mode: 'customer' | 'pin';
  customerName?: string;
  customerAddress?: string;
  accountExecutive?: string;
  pinLat?: number;
  pinLng?: number;

  // Vertical search
  selectedVertical: string;
  verticals: Vertical[];
  onVerticalChange: (id: string) => void;

  // Custom search terms (only used when selectedVertical === 'custom')
  searchTerms: string[];
  currentInput: string;
  onSearchTermsChange: (terms: string[]) => void;
  onCurrentInputChange: (value: string) => void;

  // Search state
  searchRadius: number;
  isCircleDragging: boolean;
  isSearching: boolean;
  onSearch: () => void;
  onClear: () => void;

  // Saved terms (shown in dropdown for custom mode)
  savedSearches: SavedSearch[];
  showSavedSearchesDropdown: boolean;
  onToggleSavedSearches: () => void;
  onLoadSavedSearch: (search: SavedSearch) => void;

  // Pin actions
  onDeletePin?: () => void;

  // Generated terms display (after vertical search)
  searchTermsUsed?: string[];
  searchingTerm?: string | null;
  searchProgress?: { current: number; total: number } | null;
}

const metersToMiles = (meters: number): string => {
  return (meters / 1609.34).toFixed(1);
};

const SearchPanel: React.FC<SearchPanelProps> = ({
  mode,
  customerName,
  customerAddress,
  accountExecutive,
  pinLat,
  pinLng,
  selectedVertical,
  verticals,
  onVerticalChange,
  searchTerms,
  currentInput,
  onSearchTermsChange,
  onCurrentInputChange,
  searchRadius,
  isCircleDragging,
  isSearching,
  onSearch,
  onClear,
  savedSearches,
  showSavedSearchesDropdown,
  onToggleSavedSearches,
  onLoadSavedSearch,
  onDeletePin,
  searchTermsUsed,
  searchingTerm,
  searchProgress,
}) => {
  const isCustomMode = selectedVertical === 'custom';
  const activeVertical = verticals.find(v => v.id === selectedVertical);
  const defaultTerms = activeVertical?.terms || [];

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentInput.trim()) {
      e.preventDefault();
      if (searchTerms.length >= 3) return;  // cap matches MAX_CUSTOM_TERMS below
      onSearchTermsChange([...searchTerms, currentInput.trim()]);
      onCurrentInputChange('');
    } else if (e.key === 'Backspace' && !currentInput && searchTerms.length > 0) {
      e.preventDefault();
      onSearchTermsChange(searchTerms.slice(0, -1));
    }
  };

  const removeSearchTerm = (indexToRemove: number) => {
    onSearchTermsChange(searchTerms.filter((_, index) => index !== indexToRemove));
  };

  const canSearch = isCustomMode
    ? searchTerms.length > 0
    : selectedVertical !== '';

  // Custom search is capped at 3 terms — every Places API call costs money,
  // so we let the rep tailor a search but keep blast radius small.
  const MAX_CUSTOM_TERMS = 3;
  const atCustomTermLimit = isCustomMode && searchTerms.length >= MAX_CUSTOM_TERMS;

  const renderVerticalDropdown = () => (
    <select
      className="vertical-select"
      value={selectedVertical}
      onChange={(e) => onVerticalChange(e.target.value)}
    >
      {verticals.map(v => (
        <option key={v.id} value={v.id}>{v.name}</option>
      ))}
      <option value="custom">Custom Search</option>
    </select>
  );

  return (
    <div className="bottom-search-panel">
      <div className="search-header">
        <div className="customer-info">
          {/* Header row: vertical dropdown + radius + actions */}
          <div className="customer-header">
            <div className="search-info">
              {renderVerticalDropdown()}
              <div className={`search-radius ${isCircleDragging ? 'dragging' : ''}`}>
                {metersToMiles(searchRadius)} mi
                {isCircleDragging && ' (Dragging...)'}
              </div>
              <Button
                variant="primary"
                onClick={onSearch}
                disabled={!canSearch || isSearching}
                className="search-button"
                title={
                  !canSearch
                    ? (isCustomMode ? 'Add at least one term' : 'Select a vertical to search')
                    : 'Search nearby businesses'
                }
              >
                {isSearching ? 'Searching...' : 'Search'}
              </Button>
              <Button
                variant="secondary"
                onClick={onClear}
                className="clear-search-button"
                title="Clear current search"
              >
                Clear
              </Button>
              {mode === 'pin' && onDeletePin && (
                <button
                  onClick={onDeletePin}
                  className="delete-pin-button"
                  title="Delete pin"
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* Location context line */}
          <div className="location-context">
            {mode === 'customer' ? (
              <>
                {customerName && <span>{customerName}</span>}
                {accountExecutive && accountExecutive !== 'Not Assigned' && (
                  <span> · AE: {accountExecutive}</span>
                )}
                {customerAddress && <span> · {customerAddress}</span>}
              </>
            ) : (
              <span>
                Custom pin: {pinLat?.toFixed(4)}, {pinLng?.toFixed(4)}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Custom-term input. Capped at 3 terms because every Places API call
          costs money — reps can tailor a search but blast radius stays small.
          For non-custom verticals we only show the curated term list (see
          verticals.py), no user input. */}
      {isCustomMode && (
        <div className="search-terms-input">
          <div className="search-terms-chips">
            {searchTerms.map((term, index) => (
              <div key={index} className="search-term-chip">
                {term}
                <button
                  type="button"
                  onClick={() => removeSearchTerm(index)}
                  className="remove-term"
                  aria-label={`Remove ${term}`}
                >
                  ×
                </button>
              </div>
            ))}
            <input
              type="text"
              value={currentInput}
              onChange={(e) => onCurrentInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={
                atCustomTermLimit
                  ? `Max ${MAX_CUSTOM_TERMS} terms`
                  : (searchTerms.length === 0
                      ? 'Type a term, press Enter (up to 3)'
                      : 'Add another term…')
              }
              disabled={atCustomTermLimit}
              className="term-input"
            />
          </div>
          <div className="search-terms-help">
            {searchTerms.length}/{MAX_CUSTOM_TERMS} terms · each term triggers one Places API search
          </div>
        </div>
      )}

      {/* Display search terms with progress */}
      {!isCustomMode && searchTermsUsed && searchTermsUsed.length > 0 && (
        <div className="search-terms-used">
          <div className="search-terms-used-label">
            {searchProgress
              ? `Searching (${searchProgress.current}/${searchProgress.total})...`
              : 'Search terms:'}
          </div>
          {searchTermsUsed.map((term, i) => (
            <span
              key={i}
              className={`chip-readonly${term === searchingTerm ? ' chip-active' : ''}${searchProgress && searchProgress.current > i + 1 ? ' chip-done' : ''}`}
            >
              {term}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default React.memo(SearchPanel);
